import { ErrorInfo, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Account } from '@lemontaps/lib/types/api';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '@/components';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { useAppSelector } from '@/application/hooks';
import config from '@/config/config';
import { routePaths } from '@/infrastructure/constants';
import LogoutIcon from '@mui/icons-material/Logout';
import { useLogout } from '@/infrastructure/hoc/LtAuth0Provider';

interface Props {
  errorId: string;
  error: Error;
  errorInfo: ErrorInfo;
}

const DesignError = (props: Props) => {
  const { t } = useTranslation();
  const [textMessage, setTextMessage] = useState('');
  const account = useAppSelector<Account>(state => state.account);
  const MainError = props.error.toString();
  const ErrorStacktrace = props.errorInfo.componentStack;

  const { error: authError } = useAuth0();
  const logout = useLogout();
  const isAuthError = !!authError;

  const [messageSubmitted, setMessageSubmitted] = useState(false);
  const [messageSubmitError, setMessageSubmitError] = useState(false);
  const SubmitError = useCallback(
    async (userDetails: boolean, textMessage: string) => {
      setMessageSubmitted(false);
      setMessageSubmitError(false);
      if (!props.error) return;
      axios
        .post(config.API_BASE_URL + 'monitor/error', {
          errorId: props.errorId,
          browser: (navigator?.userAgent || '').toString(),
          href: window.location.href,
          error: MainError || 'Error',
          errorStackTrace: ErrorStacktrace || 'Error stack not detected',
          accountId: account.id,
          message: (userDetails && textMessage) || undefined,
        })
        .then(res => {
          if (userDetails) {
            if (res.data?.isSuccess) {
              setMessageSubmitted(true);
            } else {
              setMessageSubmitError(true);
            }
          }
        })
        .catch(() => {
          if (userDetails) setMessageSubmitError(true);
        })
        .finally(() => setTextMessage(''));
    },
    [ErrorStacktrace, MainError, account.id, props.error, props.errorId],
  );
  useEffect(() => {
    SubmitError(false, '');
  }, [SubmitError]);

  const onHomeClickHandler = () =>
    (window.location.href =
      window.location.origin +
      (window.location.pathname === routePaths.HOME ? routePaths.EDITPROFILE : routePaths.HOME));

  return (
    <PageContainer maxWidth='m'>
      <Box display='flex' flexDirection='column' minHeight='70vh' justifyContent='center' gap={3}>
        {isAuthError ? (
          <Alert severity='error'>
            <AlertTitle>{t('loginError')}</AlertTitle>
            {authError.name}: {authError.message}
          </Alert>
        ) : (
          <Alert severity='error'>
            <AlertTitle>{t('Oops')}</AlertTitle>
            {t('anErrorHasOccured')}
          </Alert>
        )}

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreOutlined />}
            aria-controls='error-boundary-content'
            id='error-boundary-header'
          >
            <Typography variant='body1'>{t('contactUs')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('message')}
              value={textMessage}
              onChange={e => setTextMessage(e.target.value)}
              name='contactUs'
              multiline
              fullWidth
            />
            {messageSubmitted && <Typography>{t('sent')}</Typography>}
            {messageSubmitError && <Typography>Error</Typography>}
            <Box>
              <Button onClick={() => SubmitError(true, textMessage)}>{t('submitText')}</Button>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Typography>{t('loginErrorInfo')}</Typography>
        <Box display='flex' justifyContent='center' width='100%' gap={2}>
          {isAuthError ? (
            <Button onClick={() => logout()} size='large' startIcon={<LogoutIcon />}>
              {t('logout')}
            </Button>
          ) : (
            <>
              <Button onClick={onHomeClickHandler} size='large'>
                {t('goHome')}
              </Button>
              <Button onClick={() => logout()} size='large' startIcon={<LogoutIcon />}>
                {t('logout')}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </PageContainer>
  );
};

export default DesignError;
